import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import Fuse from "fuse.js";
import { useTheme } from "@/components/theme-provider";

// Layout & Display
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Alert } from "@/components/ui/alert";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Toggle } from "@/components/ui/toggle";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { Kbd } from "@/components/ui/kbd";

import {
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ChevronsUpDown,
  MoreHorizontal,
  Plus,
  Search,
  Settings,
  User,
  Mail,
  Bell,
  CircleAlert,
  TriangleAlert,
  Info,
  Check,
  Copy,
  Trash2,
  Pencil,
  Star,
  Download,
  Upload,
  ArrowRight,
  Monitor,
  Sun,
  Moon,
} from "lucide-react";

// Theme toggle for the design page
function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  const options = [
    { value: "system" as const, icon: Monitor, label: "System" },
    { value: "light" as const, icon: Sun, label: "Light" },
    { value: "dark" as const, icon: Moon, label: "Dark" },
  ];

  return (
    <div className="flex items-center rounded-lg border bg-muted/50 p-0.5">
      {options.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          onClick={() => setTheme(value)}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            theme === value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon className="size-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// Section wrapper
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <h2 className="font-mono text-xl font-semibold tracking-tight">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function SubSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="font-mono text-sm font-medium text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

const FRAMEWORKS = [
  { value: "react", label: "React" },
  { value: "vue", label: "Vue" },
  { value: "angular", label: "Angular" },
  { value: "svelte", label: "Svelte" },
  { value: "solid", label: "SolidJS" },
  { value: "qwik", label: "Qwik" },
  { value: "astro", label: "Astro" },
  { value: "next", label: "Next.js" },
];

function fuzzyFilter(value: string, search: string, keywords?: string[]) {
  const itemToRank = keywords?.length ? keywords.join(" ") : value;
  const fuse = new Fuse([itemToRank], {
    threshold: 0.4,
    includeScore: true,
  });
  const results = fuse.search(search);
  if (results.length === 0) return 0;
  // Fuse score is 0 (perfect) to 1 (no match) — invert for cmdk which wants higher = better
  return 1 - (results[0].score ?? 1);
}

/** Wraps setOpen to save/restore scroll position, preventing the browser
 *  from scrolling when base-ui moves focus into a portal-rendered popup. */
function useScrollPreservingOpen() {
  const [open, setOpenRaw] = useState(false);
  const scrollRef = useRef(0);
  const setOpen = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
    if (typeof next === "function" ? next(false) : next) {
      scrollRef.current = window.scrollY;
    }
    setOpenRaw(next);
    if (typeof next === "function" ? next(false) : next) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollRef.current, behavior: "instant" });
      });
    }
  }, []);
  return [open, setOpen] as const;
}

function SearchableSelect() {
  const [open, setOpen] = useScrollPreservingOpen();
  const [value, setValue] = useState("");

  const selected = FRAMEWORKS.find((f) => f.value === value);

  return (
    <div className="space-y-2">
      <Label>Framework</Label>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger
          render={<Button variant="outline" className="w-48 justify-between" />}
        >
          {selected ? selected.label : "Select framework..."}
          <ChevronsUpDown className="opacity-50" />
        </PopoverTrigger>
        <PopoverContent className="w-48 p-0">
          <Command filter={fuzzyFilter}>
            <CommandInput placeholder="Search..." />
            <CommandList>
              <CommandEmpty>No framework found.</CommandEmpty>
              <CommandGroup>
                {FRAMEWORKS.map((framework) => (
                  <CommandItem
                    key={framework.value}
                    value={framework.value}
                    keywords={[framework.label]}
                    onSelect={(currentValue) => {
                      setValue(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                    data-checked={value === framework.value}
                  >
                    {framework.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function SearchableSelectWithNew() {
  const [open, setOpen] = useScrollPreservingOpen();
  const [value, setValue] = useState("");
  const [items, setItems] = useState(FRAMEWORKS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const selected = items.find((f) => f.value === value);

  function handleCreate() {
    if (!newName.trim()) return;
    const newValue = newName.toLowerCase().replace(/\s+/g, "-");
    const newItem = { value: newValue, label: newName.trim() };
    setItems((prev) => [...prev, newItem]);
    setValue(newValue);
    setNewName("");
    setDialogOpen(false);
    setOpen(false);
  }

  return (
    <div className="space-y-2">
      <Label>Framework</Label>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger
          render={<Button variant="outline" className="w-48 justify-between" />}
        >
          {selected ? selected.label : "Select framework..."}
          <ChevronsUpDown className="opacity-50" />
        </PopoverTrigger>
        <PopoverContent className="w-48 p-0">
          <Command filter={fuzzyFilter}>
            <CommandInput placeholder="Search..." />
            <CommandList>
              <CommandEmpty>No framework found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setDialogOpen(true);
                    setOpen(false);
                  }}
                  className="text-primary"
                  value="__new__"
                  forceMount
                >
                  <Plus className="text-primary" />
                  New Framework
                </CommandItem>
                {items.map((framework) => (
                  <CommandItem
                    key={framework.value}
                    value={framework.value}
                    keywords={[framework.label]}
                    onSelect={(currentValue) => {
                      setValue(currentValue === value ? "" : currentValue);
                      setOpen(false);
                    }}
                    data-checked={value === framework.value}
                  >
                    {framework.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Framework</DialogTitle>
            <DialogDescription>
              Enter the name of the framework you'd like to add.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              placeholder="e.g. Remix"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DesignPage() {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [sliderValue, setSliderValue] = useState([50]);
  const [progress, setProgress] = useState(60);
  const [collapsibleOpen, setCollapsibleOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-6 py-12 space-y-16">
        {/* Page Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="font-mono text-3xl font-bold tracking-tight">
              <span className="text-primary">$</span> Design System
            </h1>
            <p className="text-muted-foreground">
              Component reference with live theme preview. Toggle between modes
              to see both sides of the system.
            </p>
          </div>
          <ThemeToggle />
        </div>

        {/* ── Buttons ── */}
        <Section title="Button">
          <SubSection title="Variants">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="default">Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="link">Link</Button>
            </div>
          </SubSection>
          <SubSection title="Sizes">
            <div className="flex flex-wrap items-center gap-2">
              <Button size="xs">Extra Small</Button>
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
              <Button size="icon">
                <Plus />
              </Button>
              <Button size="icon-sm">
                <Plus />
              </Button>
              <Button size="icon-xs">
                <Plus />
              </Button>
            </div>
          </SubSection>
          <SubSection title="With Icons">
            <div className="flex flex-wrap items-center gap-2">
              <Button>
                <Mail /> Login with Email
              </Button>
              <Button variant="outline">
                <Download /> Download
              </Button>
              <Button variant="secondary">
                Upload <Upload />
              </Button>
              <Button variant="ghost">
                <Settings /> Settings
              </Button>
            </div>
          </SubSection>
          <SubSection title="States">
            <div className="flex flex-wrap items-center gap-2">
              <Button disabled>Disabled</Button>
              <Button variant="outline" disabled>
                Disabled Outline
              </Button>
            </div>
          </SubSection>
        </Section>

        {/* ── Badge ── */}
        <Section title="Badge">
          <div className="flex flex-wrap items-center gap-2">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge variant="ghost">Ghost</Badge>
          </div>
        </Section>

        {/* ── Input ── */}
        <Section title="Input">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Default Input</Label>
              <Input placeholder="Type something..." />
            </div>
            <div className="space-y-2">
              <Label>Disabled Input</Label>
              <Input placeholder="Disabled" disabled />
            </div>
            <div className="space-y-2">
              <Label>With icon</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <Input placeholder="Search..." className="pl-8" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" placeholder="Enter password" />
            </div>
          </div>
        </Section>

        {/* ── Textarea ── */}
        <Section title="Textarea">
          <div className="max-w-md space-y-2">
            <Label>Description</Label>
            <Textarea placeholder="Enter a description..." />
          </div>
        </Section>

        {/* ── Select ── */}
        <Section title="Select">
          <SubSection title="Basic">
            <div className="space-y-2">
              <Label>Framework</Label>
              <Select defaultValue="react">
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select a framework" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="react">React</SelectItem>
                  <SelectItem value="vue">Vue</SelectItem>
                  <SelectItem value="angular">Angular</SelectItem>
                  <SelectItem value="svelte">Svelte</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </SubSection>
          <SubSection title="With Search">
            <SearchableSelect />
          </SubSection>
          <SubSection title="With Search + New">
            <SearchableSelectWithNew />
          </SubSection>
        </Section>

        {/* ── Checkbox ── */}
        <Section title="Checkbox">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox id="terms" />
              <Label htmlFor="terms">Accept terms and conditions</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="newsletter" defaultChecked />
              <Label htmlFor="newsletter">Subscribe to newsletter</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="disabled" disabled />
              <Label htmlFor="disabled" className="text-muted-foreground">
                Disabled option
              </Label>
            </div>
          </div>
        </Section>

        {/* ── Radio Group ── */}
        <Section title="Radio Group">
          <RadioGroup defaultValue="comfortable">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="default" id="r1" />
              <Label htmlFor="r1">Default</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="comfortable" id="r2" />
              <Label htmlFor="r2">Comfortable</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="compact" id="r3" />
              <Label htmlFor="r3">Compact</Label>
            </div>
          </RadioGroup>
        </Section>

        {/* ── Switch ── */}
        <Section title="Switch">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Switch id="airplane" />
              <Label htmlFor="airplane">Airplane Mode</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="notifications" defaultChecked />
              <Label htmlFor="notifications">Notifications</Label>
            </div>
          </div>
        </Section>

        {/* ── Slider ── */}
        <Section title="Slider">
          <div className="max-w-md space-y-2">
            <Label>Volume: {sliderValue[0]}%</Label>
            <Slider
              value={sliderValue}
              onValueChange={(v) =>
                setSliderValue(Array.isArray(v) ? [...v] : [v])
              }
              max={100}
              step={1}
            />
          </div>
        </Section>

        {/* ── Toggle ── */}
        <Section title="Toggle">
          <SubSection title="Single Toggle">
            <div className="flex gap-2">
              <Toggle aria-label="Toggle bold">
                <Bold />
              </Toggle>
              <Toggle aria-label="Toggle italic">
                <Italic />
              </Toggle>
              <Toggle aria-label="Toggle underline">
                <Underline />
              </Toggle>
              <Toggle variant="outline" aria-label="Toggle star">
                <Star />
              </Toggle>
            </div>
          </SubSection>
          <SubSection title="Toggle Group">
            <ToggleGroup>
              <ToggleGroupItem value="left" aria-label="Align left">
                <AlignLeft />
              </ToggleGroupItem>
              <ToggleGroupItem value="center" aria-label="Align center">
                <AlignCenter />
              </ToggleGroupItem>
              <ToggleGroupItem value="right" aria-label="Align right">
                <AlignRight />
              </ToggleGroupItem>
            </ToggleGroup>
          </SubSection>
        </Section>

        {/* ── Card ── */}
        <Section title="Card">
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Card Title</CardTitle>
                <CardDescription>
                  Card description goes here with supporting text.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p>Card content with any elements you need.</p>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline">Cancel</Button>
                <Button>Deploy</Button>
              </CardFooter>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Notifications</CardTitle>
                <CardDescription>You have 3 unread messages.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Bell className="size-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">New comment</p>
                    <p className="text-xs text-muted-foreground">
                      2 minutes ago
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="size-4 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">New message</p>
                    <p className="text-xs text-muted-foreground">1 hour ago</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </Section>

        {/* ── Alert ── */}
        <Section title="Alert">
          <div className="space-y-3">
            <Alert>
              <Info className="size-4" />
              <div>
                <p className="font-medium">Heads up!</p>
                <p className="text-sm text-muted-foreground">
                  You can add components to your app using the CLI.
                </p>
              </div>
            </Alert>
            <Alert variant="destructive">
              <CircleAlert className="size-4" />
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">
                  Your session has expired. Please log in again.
                </p>
              </div>
            </Alert>
          </div>
        </Section>

        {/* ── Accordion ── */}
        <Section title="Accordion">
          <Accordion className="max-w-lg">
            <AccordionItem value="item-1">
              <AccordionTrigger>Is it accessible?</AccordionTrigger>
              <AccordionContent>
                Yes. It adheres to the WAI-ARIA design pattern.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>Is it styled?</AccordionTrigger>
              <AccordionContent>
                Yes. It comes with default styles that match the other
                components' aesthetic.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>Is it animated?</AccordionTrigger>
              <AccordionContent>
                Yes. It's animated by default, but you can disable it if you
                prefer.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Section>

        {/* ── Tabs ── */}
        <Section title="Tabs">
          <SubSection title="Default">
            <Tabs defaultValue="account" className="max-w-lg">
              <TabsList>
                <TabsTrigger value="account">Account</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="account">
                <Card>
                  <CardHeader>
                    <CardTitle>Account</CardTitle>
                    <CardDescription>
                      Make changes to your account here.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Label>Name</Label>
                    <Input defaultValue="John Doe" />
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="password">
                <Card>
                  <CardHeader>
                    <CardTitle>Password</CardTitle>
                    <CardDescription>
                      Change your password here.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Label>Current password</Label>
                    <Input type="password" />
                  </CardContent>
                </Card>
              </TabsContent>
              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle>Settings</CardTitle>
                    <CardDescription>Manage your preferences.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <Switch />
                      <Label>Dark mode</Label>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </SubSection>
          <SubSection title="Line variant">
            <Tabs defaultValue="overview" className="max-w-lg">
              <TabsList variant="line">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="analytics">Analytics</TabsTrigger>
                <TabsTrigger value="reports">Reports</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="pt-4">
                Overview content goes here.
              </TabsContent>
              <TabsContent value="analytics" className="pt-4">
                Analytics content goes here.
              </TabsContent>
              <TabsContent value="reports" className="pt-4">
                Reports content goes here.
              </TabsContent>
            </Tabs>
          </SubSection>
        </Section>

        {/* ── Table ── */}
        <Section title="Table">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Invoice</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">INV001</TableCell>
                  <TableCell>
                    <Badge variant="secondary">Paid</Badge>
                  </TableCell>
                  <TableCell>Credit Card</TableCell>
                  <TableCell className="text-right">$250.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV002</TableCell>
                  <TableCell>
                    <Badge variant="outline">Pending</Badge>
                  </TableCell>
                  <TableCell>PayPal</TableCell>
                  <TableCell className="text-right">$150.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV003</TableCell>
                  <TableCell>
                    <Badge variant="destructive">Overdue</Badge>
                  </TableCell>
                  <TableCell>Bank Transfer</TableCell>
                  <TableCell className="text-right">$350.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV004</TableCell>
                  <TableCell>
                    <Badge variant="secondary">Paid</Badge>
                  </TableCell>
                  <TableCell>Credit Card</TableCell>
                  <TableCell className="text-right">$450.00</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Section>

        {/* ── Dialog ── */}
        <Section title="Dialog">
          <div className="flex flex-wrap gap-2">
            <Dialog>
              <DialogTrigger render={<Button variant="outline" />}>
                Open Dialog
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Profile</DialogTitle>
                  <DialogDescription>
                    Make changes to your profile here. Click save when you're
                    done.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input defaultValue="John Doe" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input defaultValue="john@example.com" />
                  </div>
                </div>
                <DialogFooter>
                  <Button>Save changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </Section>

        {/* ── Alert Dialog ── */}
        <Section title="Alert Dialog">
          <AlertDialog>
            <AlertDialogTrigger render={<Button variant="destructive" />}>
              Delete Account
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete
                  your account and remove your data from our servers.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction>Continue</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Section>

        {/* ── Sheet ── */}
        <Section title="Sheet">
          <div className="flex flex-wrap gap-2">
            <Sheet>
              <SheetTrigger render={<Button variant="outline" />}>
                Open Right Sheet
              </SheetTrigger>
              <SheetContent side="right">
                <SheetHeader>
                  <SheetTitle>Sheet Title</SheetTitle>
                  <SheetDescription>
                    This is a sheet that slides in from the right.
                  </SheetDescription>
                </SheetHeader>
                <div className="p-4 space-y-3">
                  <Label>Name</Label>
                  <Input placeholder="Enter your name" />
                  <Label>Email</Label>
                  <Input placeholder="Enter your email" />
                </div>
              </SheetContent>
            </Sheet>
            <Sheet>
              <SheetTrigger render={<Button variant="outline" />}>
                Open Left Sheet
              </SheetTrigger>
              <SheetContent side="left">
                <SheetHeader>
                  <SheetTitle>Navigation</SheetTitle>
                  <SheetDescription>Browse the app sections.</SheetDescription>
                </SheetHeader>
              </SheetContent>
            </Sheet>
          </div>
        </Section>

        {/* ── Drawer ── */}
        <Section title="Drawer">
          <Drawer>
            <DrawerTrigger asChild>
              <Button variant="outline">Open Drawer</Button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader>
                <DrawerTitle>Move Goal</DrawerTitle>
                <DrawerDescription>
                  Set your daily activity goal.
                </DrawerDescription>
              </DrawerHeader>
              <div className="p-4">
                <div className="space-y-2">
                  <Label>Goal: {sliderValue[0]} calories</Label>
                  <Slider
                    value={sliderValue}
                    onValueChange={(v) =>
                      setSliderValue(Array.isArray(v) ? [...v] : [v])
                    }
                    max={500}
                    step={10}
                  />
                </div>
              </div>
              <DrawerFooter>
                <Button>Submit</Button>
                <DrawerClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DrawerClose>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </Section>

        {/* ── Dropdown Menu ── */}
        <Section title="Dropdown Menu">
          <DropdownMenu>
            <DropdownMenuTrigger render={<Button variant="outline" />}>
              <MoreHorizontal /> Options
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuGroup>
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuItem>
                  <User /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Mail /> Messages
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem variant="destructive">
                  <Trash2 /> Delete
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </Section>

        {/* ── Popover ── */}
        <Section title="Popover">
          <Popover modal={false}>
            <PopoverTrigger render={<Button variant="outline" />}>
              <Settings /> Open Popover
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-3">
                <h4 className="font-medium">Dimensions</h4>
                <div className="grid gap-2">
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label>Width</Label>
                    <Input defaultValue="100%" className="col-span-2" />
                  </div>
                  <div className="grid grid-cols-3 items-center gap-4">
                    <Label>Height</Label>
                    <Input defaultValue="25px" className="col-span-2" />
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </Section>

        {/* ── Hover Card ── */}
        <Section title="Hover Card">
          <HoverCard>
            <HoverCardTrigger render={<Button variant="link" />}>
              @shadcn
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="flex gap-4">
                <Avatar className="size-10">
                  <span className="flex size-full items-center justify-center rounded-full bg-muted text-sm font-medium">
                    SC
                  </span>
                </Avatar>
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">@shadcn</h4>
                  <p className="text-sm text-muted-foreground">
                    The React component library. Open source and customizable.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Joined December 2021
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </Section>

        {/* ── Tooltip ── */}
        <Section title="Tooltip">
          <div className="flex gap-2">
            <Tooltip>
              <TooltipTrigger render={<Button variant="outline" size="icon" />}>
                <Plus />
              </TooltipTrigger>
              <TooltipContent>Add new item</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger render={<Button variant="outline" size="icon" />}>
                <Pencil />
              </TooltipTrigger>
              <TooltipContent>
                Edit <Kbd>E</Kbd>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger render={<Button variant="outline" size="icon" />}>
                <Copy />
              </TooltipTrigger>
              <TooltipContent side="bottom">Copy to clipboard</TooltipContent>
            </Tooltip>
          </div>
        </Section>

        {/* ── Breadcrumb ── */}
        <Section title="Breadcrumb">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Components</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Breadcrumb</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </Section>

        {/* ── Pagination ── */}
        <Section title="Pagination">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious href="#" />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive>
                  1
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#">2</PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#">3</PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
              <PaginationItem>
                <PaginationNext href="#" />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </Section>

        {/* ── Progress ── */}
        <Section title="Progress">
          <div className="max-w-md space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span className="text-muted-foreground">{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProgress(Math.max(0, progress - 10))}
              >
                -10
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProgress(Math.min(100, progress + 10))}
              >
                +10
              </Button>
            </div>
          </div>
        </Section>

        {/* ── Skeleton ── */}
        <Section title="Skeleton">
          <div className="flex items-center gap-4">
            <Skeleton className="size-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-[250px]" />
              <Skeleton className="h-4 w-[200px]" />
            </div>
          </div>
        </Section>

        {/* ── Spinner ── */}
        <Section title="Spinner">
          <div className="flex items-center gap-4">
            <Spinner className="size-4" />
            <Spinner className="size-6" />
            <Spinner className="size-8" />
          </div>
        </Section>

        {/* ── Avatar ── */}
        <Section title="Avatar">
          <div className="flex items-center gap-3">
            <Avatar className="size-8">
              <span className="flex size-full items-center justify-center rounded-full bg-muted text-xs font-medium">
                JD
              </span>
            </Avatar>
            <Avatar className="size-10">
              <span className="flex size-full items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                AB
              </span>
            </Avatar>
            <Avatar className="size-12">
              <span className="flex size-full items-center justify-center rounded-full bg-secondary text-secondary-foreground font-medium">
                CD
              </span>
            </Avatar>
          </div>
        </Section>

        {/* ── Separator ── */}
        <Section title="Separator">
          <div className="max-w-md">
            <div>
              <h4 className="text-sm font-medium">shadcn/ui</h4>
              <p className="text-sm text-muted-foreground">
                An open-source UI component library.
              </p>
            </div>
            <Separator className="my-4" />
            <div className="flex h-5 items-center gap-4 text-sm">
              <span>Blog</span>
              <Separator orientation="vertical" />
              <span>Docs</span>
              <Separator orientation="vertical" />
              <span>Source</span>
            </div>
          </div>
        </Section>

        {/* ── Aspect Ratio ── */}
        <Section title="Aspect Ratio">
          <div className="max-w-sm">
            <AspectRatio ratio={16 / 9}>
              <div className="flex size-full items-center justify-center rounded-lg bg-muted text-muted-foreground">
                16:9
              </div>
            </AspectRatio>
          </div>
        </Section>

        {/* ── Scroll Area ── */}
        <Section title="Scroll Area">
          <ScrollArea className="h-48 w-64 rounded-lg border p-4">
            <div className="space-y-4">
              {Array.from({ length: 20 }, (_, i) => (
                <div key={i} className="text-sm">
                  Item {i + 1} — Scrollable content
                </div>
              ))}
            </div>
          </ScrollArea>
        </Section>

        {/* ── Collapsible ── */}
        <Section title="Collapsible">
          <Collapsible
            open={collapsibleOpen}
            onOpenChange={setCollapsibleOpen}
            className="max-w-sm space-y-2"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">
                @peduarte starred 3 repositories
              </h4>
              <CollapsibleTrigger
                render={<Button variant="ghost" size="icon-sm" />}
              >
                <ChevronsUpDown />
              </CollapsibleTrigger>
            </div>
            <div className="rounded-md border px-3 py-2 text-sm">
              @radix-ui/primitives
            </div>
            <CollapsibleContent className="space-y-2">
              <div className="rounded-md border px-3 py-2 text-sm">
                @radix-ui/colors
              </div>
              <div className="rounded-md border px-3 py-2 text-sm">
                @stitches/react
              </div>
            </CollapsibleContent>
          </Collapsible>
        </Section>

        {/* ── Calendar ── */}
        <Section title="Calendar">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            className="rounded-lg border w-fit"
          />
        </Section>

        {/* ── Toast (Sonner) ── */}
        <Section title="Toast (Sonner)">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => toast("Event has been created")}
            >
              Default Toast
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.success("Successfully saved!")}
            >
              <Check /> Success
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.error("Something went wrong")}
            >
              <CircleAlert /> Error
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.warning("Please review your input")}
            >
              <TriangleAlert /> Warning
            </Button>
            <Button
              variant="outline"
              onClick={() => toast.info("New update available")}
            >
              <Info /> Info
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast("Event created", {
                  description: "Monday, January 3rd at 6:00pm",
                })
              }
            >
              With Description
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                toast("Event created", {
                  action: { label: "Undo", onClick: () => console.log("Undo") },
                })
              }
            >
              With Action
            </Button>
          </div>
        </Section>

        {/* ── Kbd ── */}
        <Section title="Keyboard Shortcut">
          <div className="flex flex-wrap items-center gap-3">
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
            <span className="text-sm text-muted-foreground">or</span>
            <span className="flex items-center gap-1">
              <Kbd>Ctrl</Kbd>
              <span className="text-sm text-muted-foreground">+</span>
              <Kbd>C</Kbd>
            </span>
          </div>
        </Section>

        {/* ── Combined Example ── */}
        <Section title="Combined Example">
          <Card className="max-w-lg">
            <CardHeader>
              <CardTitle>Create Project</CardTitle>
              <CardDescription>
                Deploy your new project in one-click.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Project Name</Label>
                <Input placeholder="my-awesome-project" />
              </div>
              <div className="space-y-2">
                <Label>Framework</Label>
                <Select defaultValue="next">
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="next">Next.js</SelectItem>
                    <SelectItem value="remix">Remix</SelectItem>
                    <SelectItem value="astro">Astro</SelectItem>
                    <SelectItem value="gatsby">Gatsby</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea placeholder="Describe your project..." />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="public" />
                <Label htmlFor="public">Make this project public</Label>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline">Cancel</Button>
              <Button>
                <ArrowRight /> Create Project
              </Button>
            </CardFooter>
          </Card>
        </Section>

        {/* Footer */}
        <div className="border-t pt-8 text-center text-sm text-muted-foreground font-mono">
          <p>// end of line</p>
        </div>
      </div>
    </div>
  );
}
